import { fabric } from 'fabric';
import { useAppContext } from '../../context/app-context';
import { storePreviews } from '../../context/app-context/app-context-actions';
import { useCardContext } from '../../context/card-context';
import { useInitializationDataContext } from '../../context/data-context';
import { CardType } from '../../global-types';
import { PreviewImage } from '../../global-types/images';
import { useFeatureFlags, useIsSignAndSend } from '../../hooks';
import { useIsOneToMany } from '../../hooks/use-is-one-to-many';
import { useS3Upload } from '../../hooks/use-s3-upload';
import { config } from '../../regional-config';
import { getAssetUrls } from '../../services/image';
import {
  cleanFoldLines,
  cleanPlaceholders,
  cleanUneditedTexts,
  cleanEditableAreaBorders,
  removeEditIconsFromCanvas,
  removePhotoTextZonesFromCanvas,
  TransformedPersonalizationData,
} from '../helper-settings';
import { getPreviewLabel } from './get-preview-label';
import { saveLinkedAssets } from './save-linked-assets';
import { uploadPreviewImage } from './upload-preview-image';

export const useGeneratePreviews = () => {
  const { uploadFile } = useS3Upload();
  const { appDispatch } = useAppContext();
  const {
    cardState: { cardFacesList },
    cardDispatch,
  } = useCardContext();
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();
  const isOneToMany = useIsOneToMany();
  const { CANVAS_JSON_PREVIEW } = useFeatureFlags();
  const isSignAndSend = useIsSignAndSend();
  const { skipAddressView } = config;
  const isFlatCard = cardFacesList.length === 2;

  /**
   * Begin preview generating process by firing one of two functions based on if we want to use printJson
   *
   * @param saveData A list of card faces with printJson, which we use for printJson previews only
   * @returns void
   */
  const generatePreviewImages = async (saveData: TransformedPersonalizationData[]) => {
    return CANVAS_JSON_PREVIEW ? generateCanvasJsonPreview() : generatePrintJsonPreview(saveData);
  };

  /**
   * Generates preview images using printJson
   *
   * @param saveData A list of card faces with printJson, which we use for printJson previews only
   * @returns void
   */
  const generatePrintJsonPreview = async (saveData: TransformedPersonalizationData[]) => {
    if (isSignAndSend) {
      // We generate a preview only for index 2, inside right, for S&S
      saveData = [saveData[2]];
    }
    if (!initializedData) {
      throw new Error('Error generating preview due to missing initialization data');
    }
    // previewCount: 1 for S&S, 2 for 1-to-many and POD
    const previewCount = isSignAndSend ? 1 : 2;
    const previewImages: PreviewImage[] = [];
    // Generate asset-storage-urls for each face requiring new preview
    const assetStorageUrls = await getAssetUrls(previewCount, 'png');
    let generatedPreviewCount = 0;
    let faceIndex = 0;
    for (const cardFace of saveData) {
      const index = faceIndex;
      faceIndex += 1;
      const lastFace = index === saveData.length - 1;
      const podOneToOne = !isSignAndSend && !isOneToMany;
      // Don't generate preview for last card face (envelope on 1-to-1 POD or when skipping address view)
      if (lastFace && (skipAddressView || podOneToOne)) continue;
      const newCanvas = new fabric.Canvas(null, {
        width: cardFace?.PrintJson?.backgroundImage?.width,
        height: cardFace?.PrintJson?.backgroundImage?.height,
      });

      const image = await new Promise<string>((resolve, reject) => {
        newCanvas.loadFromJSON(cardFace.PrintJson, () => {
          try {
            cleanEditableAreaBorders(newCanvas);
            newCanvas.discardActiveObject();
            newCanvas.renderAll();
            resolve(
              newCanvas.toDataURL({
                format: 'png',
              }),
            );
          } catch (error) {
            reject(error);
          }
        });
      });
      previewImages[`${index}`] = {
        dataURL: image,
        type: getPreviewLabel(initializedData.project_type_code as CardType, index, isFlatCard),
      };
      await uploadPreviewImage(assetStorageUrls, image, index, uploadFile);
      generatedPreviewCount++;
    }

    if (generatedPreviewCount > 0) {
      await linkPreviewAssets(previewImages, assetStorageUrls);
      storePreviews(appDispatch, previewImages);
    }
  };

  /**
   * Generates preview images using canvasJson.
   * Uses every face with a valid canvas reference; S&S no longer forces only
   * inside-right because front/inside-left can also contain current canvas state.
   *
   * @returns void
   */
  const generateCanvasJsonPreview = async () => {
    const previewImages: PreviewImage[] = [];
    const faces = cardFacesList
      .map((cardFace, originalIndex) => ({ cardFace, originalIndex }))
      .filter(({ cardFace, originalIndex }) => {
        if (!cardFace.canvas?.current) {
          return false;
        }
        return isSignAndSend || isOneToMany || originalIndex !== cardFacesList.length - 1;
      });

    if (!initializedData) {
      throw new Error('Error generating preview due to missing initialization data');
    }
    const projectTypeCode = initializedData.project_type_code as CardType;
    const previewCount = faces.length;
    // Generate asset-storage-urls for each face requiring new preview
    const assetStorageUrls = await getAssetUrls(previewCount, 'png');
    let uploadIndex = 0;
    for (const { cardFace, originalIndex } of faces) {
      const index = uploadIndex;
      uploadIndex += 1;
      let previewImage;
      // Since we're using canvasJson, we can get the canvas from the state
      const myCanvas = cardFace.canvas.current;
      if (myCanvas) {
        previewImage = await canvasToDataUrl(myCanvas);
      }
      if (previewImage) {
        previewImages[`${index}`] = {
          dataURL: previewImage,
          type: getCanvasJsonPreviewLabel(originalIndex, projectTypeCode),
        };
      } else {
        throw new Error(`Preview generation failed: no image for cardface index ${originalIndex}`);
      }
      // Use generated asset storage url to upload preview image to S3
      await uploadPreviewImage(assetStorageUrls, previewImage, index, uploadFile);
    }
    await linkPreviewAssets(previewImages, assetStorageUrls);
    storePreviews(appDispatch, previewImages);
  };

  const linkPreviewAssets = async (previewImages, assetStorageUrls) => {
    // All faces are uploaded, so we can save linked assets
    if (isSignAndSend && initializedData) {
      const { Faces } = initializedData.variables.template_data;
      // Front/inside-left are generated when their canvases are available;
      // fall back to template backgrounds only for non-editable/missing canvases.
      const frontPreview =
        previewImages.find((image) => image?.type === 'Front') ?? getFallbackPreview(Faces[0]?.BackgroundUrl, 'Front');
      const insideLeftPreview =
        previewImages.find((image) => image?.type === 'Inside Left') ??
        getFallbackPreview(Faces[1]?.BackgroundUrl, 'Inside Left');
      const generatedPreviews = previewImages.filter(
        (image) => image?.type !== 'Front' && image?.type !== 'Inside Left',
      );
      const staticPreviews = [frontPreview, insideLeftPreview].filter((preview): preview is PreviewImage =>
        Boolean(preview),
      );
      previewImages.splice(0, previewImages.length, ...staticPreviews, ...generatedPreviews);
    }
    if (initializedData) {
      await saveLinkedAssets(assetStorageUrls, initializedData, cardDispatch, isOneToMany, previewImages);
    } else {
      throw new Error('Error: Missing initializedData, cannot save assets');
    }
  };

  /** Returns a static fallback preview only when a background URL exists. */
  const getFallbackPreview = (dataURL: string | undefined, type: string): PreviewImage | undefined =>
    dataURL ? { dataURL, type } : undefined;

  /**
   * Gets the preview label from the original card face index.
   * S&S labels must keep front/inside-left/inside-right order even when
   * invalid canvas faces are filtered out before upload.
   *
   * @param index Original index in cardFacesList
   * @returns Preview label for the generated canvas image
   */
  const getCanvasJsonPreviewLabel = (index: number, projectTypeCode: CardType) => {
    if (isSignAndSend) {
      switch (index) {
        case 0:
          return 'Front';
        case 1:
          return 'Inside Left';
        case 2:
          return 'Inside Right';
        default:
          return getPreviewLabel(projectTypeCode, index, isFlatCard);
      }
    }
    return getPreviewLabel(projectTypeCode, index, isFlatCard);
  };

  /**
   * Genereates dataUrl from canvas, part of canvasJson preview flow
   *
   * @param saveData A list of card faces with printJson, which we use for printJson previews only
   * @returns void
   */
  const canvasToDataUrl = async (canvas: fabric.Canvas) => {
    if (!canvas) {
      return '';
    }
    // Export from a throwaway canvas so the live editor canvas (its objects, handlers and
    // interaction flags) is never mutated. Cleaning happens only on this disposable copy.
    const json = canvas.toJSON(['name', 'data']);
    const viewportTransform = canvas.viewportTransform ? [...canvas.viewportTransform] : undefined;
    const previewCanvas = new fabric.Canvas(null, {
      width: canvas.getWidth(),
      height: canvas.getHeight(),
    });
    try {
      await new Promise<void>((resolve) => {
        previewCanvas.loadFromJSON(json, () => resolve());
      });
      // Match the live canvas zoom/pan so the exported image keeps the same framing.
      if (viewportTransform) {
        previewCanvas.setViewportTransform(viewportTransform);
      }
      cleanPreviewCanvasJson(previewCanvas);
      previewCanvas.discardActiveObject();
      previewCanvas.renderAll();
      return previewCanvas.toDataURL({
        format: 'png',
        multiplier: 2,
      });
    } finally {
      previewCanvas.dispose();
    }
  };

  /**
   * Cleans canvasJson prior to generating preview images
   *
   * @param saveData A list of card faces with printJson, which we use for printJson previews only
   * @returns void
   */
  const cleanPreviewCanvasJson = (canvas: fabric.Canvas) => {
    if (canvas) {
      cleanPlaceholders(canvas);
      cleanFoldLines(canvas);
      cleanUneditedTexts(canvas);
      cleanEditableAreaBorders(canvas);
      removePhotoTextZonesFromCanvas(canvas);
      removeEditIconsFromCanvas(canvas);
    }
  };

  return {
    generatePreviewImages,
  };
};
